import { useEffect, useRef } from 'react'
import { ChatCircleTextIcon } from '@phosphor-icons/react'
import { Button } from 'app/components/ui/button'
import { SMS_CONSENT_CANCELLED_MESSAGE } from 'app/lib/workflows/smsConsent'

type SmsConsentBarProps = {
  /** Bumped each time the user tries to turn the workflow on while this is showing. Every new
   *  value replays the wiggle and puts focus on the button, so keyboard users land on it too. */
  nudge: number
  onOpen: () => void
}

/**
 * The consent modal, collapsed. Closing the modal leaves this at the bottom of the canvas until the
 * workspace confirms, so the question stays one click away instead of vanishing with a toast.
 */
export function SmsConsentBar({ nudge, onOpen }: SmsConsentBarProps) {
  const button = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (nudge > 0) button.current?.focus()
  }, [nudge])

  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-4 z-30 flex justify-center px-4">
      <div
        // A new key remounts the card, which is what replays a CSS animation.
        key={nudge}
        role="status"
        className={`pointer-events-auto flex max-w-full items-center gap-3 rounded-xl border border-default bg-surface py-2 pr-2 pl-3.5 shadow-lg animate-in fade-in slide-in-from-bottom-2 ${
          nudge > 0 ? `motion-safe:animate-wiggle motion-reduce:ring-2 motion-reduce:ring-primary-600` : ``
        }`}
      >
        <ChatCircleTextIcon className="size-4 shrink-0 text-subtle" aria-hidden />
        <p className="min-w-0 text-xs text-default">{SMS_CONSENT_CANCELLED_MESSAGE}</p>
        <Button ref={button} size="xs" className="shrink-0" onClick={onOpen}>
          Review and confirm
        </Button>
      </div>
    </div>
  )
}
